import React, { useMemo } from 'react';
import StatePill from '../StatePill';
import {
  locName, isArchived, siteStatusLabel, devicesAtSite, consumablesAtSite,
  consumableTotal, codedTypeNames, devicesSince, lastDeployment,
  relativeDays, formatDate, subLocationsFor, deviceSubLocation,
} from '../../utils/locations';
// Site detail, per the Locations redesign.
//
// Layout is two columns: the sub-locations on the site take the main column,
// because that is what someone opens this page to check. The right column holds
// the reference material — who to call, the site's fixed details, and what has
// moved recently.
//
// Everything here reads from stored state. The mockup's hardcoded timeline is
// replaced with the real dispatch/return history.
//
// There is deliberately no per-device list here. The site page is a summary of
// the places within a site; the devices themselves are listed on the
// sub-location page (SubLocationDetail) and on the Stock screen, which is where
// you go to work through them. Repeating them here made the page unbounded and
// duplicated both of those.

function Card({ title, count, badge, children, style, bodyStyle }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', ...style }}>
      <div style={{ height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px', borderBottom: '1px solid var(--border-default)' }}>
        <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>{title}</span>
        {badge}
        {count != null && <span className="tnum" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{count}</span>}
      </div>
      <div style={{ flex: 1, minHeight: 0, ...bodyStyle }}>{children}</div>
    </div>
  );
}

function LocationDetail({
  loc, st, onBack, onEdit, onToggleArchive,
  onDeployHere, onOpenSubLocation, onAddSubLocation, onEditSubLocation,
  onTransferSubLocation, onMoveIntoLocation, isReadOnly, canArchive,
}) {
  const lname = locName(loc);
  const archived = isArchived(loc);

  // Every device on this site, resolved to its sub-location. Only the project
  // and the count are needed now that the device list has gone: the
  // sub-locations card shows per-project counts, and the footer below it counts
  // the ones on no project at all.
  //
  // deviceSubLocation reads both mechanisms — a manual tag on the device, and the
  // derived portal project — so `st` is the dep: the grouping must refresh after a
  // portal sync and after a manual tag alike.
  const deviceRows = useMemo(() => {
    return devicesAtSite(st.devices || [], loc).map(d => ({
      device: d,
      project: deviceSubLocation(d, st),
    }));
  }, [loc, st]);

  const portalAsOf = st.portalSnapshot?.fetchedAt || null;

  // The site's sub-locations: stored ones and portal projects mapped here, merged by
  // subLocationsFor — the same helper deviceSubLocation defers to, so the card and the
  // device counts can never disagree. One with nothing on it still gets a row: an empty
  // sub-location is a real state worth seeing.
  const subs = useMemo(
    () => subLocationsFor(loc, st),
    [loc, st]
  );
  const unassigned = deviceRows.filter(r => !r.project).length;
  const openSub = sub => { if (onOpenSubLocation) onOpenSubLocation(sub); };

  const held = consumablesAtSite(loc, st.consumableStock || [], st.consumableQty || {}, st.consumables || []);
  const heldTotal = consumableTotal(loc, st.consumableStock || [], st.consumableQty || {}, st.consumables || []);
  const coded = codedTypeNames(st.consumableStock || []);
  const heldRows = Object.entries(held).sort((a, b) => b[1] - a[1]);
  const codedHeld = heldRows.filter(([name]) => coded.has(name)).map(([name]) => name);

  const since = devicesSince(loc);
  const last = lastDeployment(loc);

  const facts = [
    { label: 'Site ID', value: loc.siteId || '—' },
    { label: 'Client', value: loc.customer || 'Not set' },
    { label: 'Region', value: loc.address || 'Not set' },
    { label: 'Devices since', value: since ? formatDate(since) : '—' },
    { label: 'Last deployment', value: relativeDays(last) },
    { label: 'Deployable', value: archived ? 'No — archived sites are hidden' : 'Yes' },
  ];

  // Recent movements, from the real batch history rather than a fixed script.
  const events = useMemo(() => {
    const out = [];
    ((loc.batches) || []).forEach(b => {
      const n = (b.deviceIds || []).length;
      if (b.dispatchedAt) {
        out.push({
          text: n ? `${n} device${n === 1 ? '' : 's'} dispatched to site` : 'Batch dispatched to site',
          when: new Date(b.dispatchedAt),
          tone: 'info',
        });
      }
      if (b.returnedAt) {
        out.push({ text: `Batch ${b.number || ''} returned to stock`.replace('  ', ' '), when: new Date(b.returnedAt), tone: 'neutral' });
      }
    });
    out.sort((a, z) => z.when - a.when);
    return out.slice(0, 6);
  }, [loc.batches]);

  const subBtn = {
    display: 'inline-flex', alignItems: 'center', gap: 5, height: 26, padding: '0 10px',
    borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--surface-default)',
    color: 'var(--text-primary)', fontFamily: 'inherit', fontSize: 12.5, cursor: 'pointer',
  };

  const btnGhost = {
    flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px',
    borderRadius: 6, border: '1px solid var(--border-default)', background: 'var(--surface-default)',
    color: 'var(--text-primary)', fontFamily: 'inherit', fontSize: 14, fontWeight: 500, cursor: 'pointer',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <button type="button" onClick={onBack} style={{ ...btnGhost, marginTop: 2 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.75 }}><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
          Locations
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-.3px', color: 'var(--text-primary)' }}>{lname}</span>
            <StatePill state={siteStatusLabel(loc)} tone={archived ? 'neutral' : 'success'} />
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 4 }}>
            {loc.customer || 'No client'} · {loc.address || 'No region'} · <span className="tnum">{loc.siteId || '—'}</span>
          </div>
        </div>
        {!isReadOnly && (
          <button type="button" onClick={onEdit} style={btnGhost}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.75 }}><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
            Edit site
          </button>
        )}
        {!isReadOnly && !archived && onMoveIntoLocation && (
          <button type="button" onClick={onMoveIntoLocation} style={btnGhost}
            title={`Make ${lname} a sub-location of another location`}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.75 }}><polyline points="9 10 4 15 9 20" /><path d="M20 4v7a4 4 0 0 1-4 4H4" /></svg>
            Move into location
          </button>
        )}
        {!isReadOnly && (
          <button
            type="button"
            onClick={onToggleArchive}
            disabled={!archived && !canArchive}
            title={!archived && !canArchive ? 'Return all devices before archiving' : undefined}
            style={{
              ...btnGhost,
              color: archived ? 'var(--success-text)' : 'var(--danger-text)',
              opacity: (!archived && !canArchive) ? 0.45 : 1,
              cursor: (!archived && !canArchive) ? 'not-allowed' : 'pointer',
            }}>
            {archived ? 'Restore site' : 'Archive site'}
          </button>
        )}
        {!isReadOnly && !archived && (
          <button type="button" onClick={onDeployHere} className="btn btn-primary" style={{ height: 32 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>
            Deploy here
          </button>
        )}
      </div>

      {/* Body */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 460px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Sub-locations. Shown once the site holds one, or whenever one can be added —
              a read-only view of a site with none reads as it did before this existed. */}
          {(subs.length > 0 || (!isReadOnly && onAddSubLocation)) && (
            <Card
              title="Sub-locations"
              count={subs.length || null}
              badge={!isReadOnly && (onAddSubLocation || onTransferSubLocation) && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {/* Only worth offering once there is somewhere to move devices between. */}
                  {onTransferSubLocation && subs.some(s => s.origin === 'manual') && deviceRows.length > 0 && (
                    <button type="button" onClick={onTransferSubLocation} style={subBtn}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" /><polyline points="21 16 21 21 16 21" /><line x1="15" y1="15" x2="21" y2="21" /><line x1="4" y1="4" x2="9" y2="9" /></svg>
                      Move devices
                    </button>
                  )}
                  {onAddSubLocation && (
                    <button type="button" onClick={onAddSubLocation} style={subBtn}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                      Add
                    </button>
                  )}
                </span>
              )}
              style={{ flexShrink: 0 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 90px 56px', gap: '0 16px', height: 34, alignItems: 'center', padding: '0 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)', fontSize: 11.5, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                <div>Project</div>
                <div style={{ textAlign: 'right' }}>Devices</div>
                <div />
              </div>
              {subs.length === 0 && (
                <div style={{ padding: '18px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>No sub-locations yet</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    Add one to split this location into the places within it, then tag devices with it.
                  </div>
                </div>
              )}
              {subs.map(s => (
                <div
                  key={s.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openSub(s)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openSub(s); } }}
                  style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 90px 56px', gap: '0 16px', alignItems: 'center', minHeight: 44, padding: '0 16px', borderBottom: '1px solid var(--border-subtle)', cursor: onOpenSubLocation ? 'pointer' : 'default' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-raised)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                  <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                    {s.origin === 'manual' && (
                      <span className="pill pill-neutral" style={{ flexShrink: 0, fontSize: 10.5 }}>Manual</span>
                    )}
                  </div>
                  <div className="tnum" style={{ textAlign: 'right', fontSize: 14, color: s.deviceCount ? 'var(--text-primary)' : 'var(--text-muted)' }}>{s.deviceCount}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, color: 'var(--text-subtle)' }}>
                    {/* Only a manual sub-location is editable — a portal project's name and
                        parent are owned by the portal. stopPropagation so the pencil does
                        not also open the row. */}
                    {!isReadOnly && onEditSubLocation && s.origin === 'manual' && (
                      <button
                        type="button"
                        aria-label={`Edit ${s.name}`}
                        onClick={e => { e.stopPropagation(); onEditSubLocation(s.stored); }}
                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, border: 'none', borderRadius: 6, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', padding: 0 }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                      </button>
                    )}
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                  </div>
                </div>
              ))}
              <div style={{ padding: '10px 16px', background: 'var(--surface-inset)', fontSize: 12, color: 'var(--text-muted)' }}>
                {unassigned > 0
                  ? `${unassigned} device${unassigned === 1 ? '' : 's'} on this site ${unassigned === 1 ? 'is' : 'are'} not on any project.`
                  : 'Every device on this site sits on one of these projects.'}
                {subs.some(x => x.origin === 'portal') && portalAsOf
                  ? ` Portal sub-locations last synced ${formatDate(portalAsOf)}.`
                  : ''}
              </div>
            </Card>
          )}

          <Card title="Consumables held" count={heldTotal} style={{ flexShrink: 0 }}>
            {heldRows.length === 0 ? (
              <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--text-muted)' }}>No consumables held at this site.</div>
            ) : (
              <>
                {heldRows.map(([name, qty]) => (
                  <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 38, padding: '0 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                    <span style={{ fontSize: 14, color: 'var(--text-primary)', flex: 1, minWidth: 0 }}>{name}</span>
                    {coded.has(name) && (
                      <span className="pill pill-neutral" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5 }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><line x1="14" y1="14" x2="14" y2="14" /><line x1="21" y1="21" x2="21" y2="21" /></svg>
                        Coded
                      </span>
                    )}
                    <span className="tnum" style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', width: 44, textAlign: 'right' }}>{qty}</span>
                  </div>
                ))}
                <div style={{ padding: '10px 16px', background: 'var(--surface-inset)', fontSize: 12, color: 'var(--text-muted)' }}>
                  {codedHeld.length
                    ? `${codedHeld.join(' and ')} ${codedHeld.length === 1 ? 'is' : 'are'} tracked individually, so each unit here carries its own QR code.`
                    : 'None of these are individually tracked — they are counted, not serialised.'}
                </div>
              </>
            )}
          </Card>
        </div>

        <div style={{ flex: '0 1 320px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card title="Site contact" style={{ flexShrink: 0 }}>
            {loc.contact || loc.phone || loc.email ? (
              <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{loc.contact || 'Not set'}</div>
                  {loc.contactRole && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{loc.contactRole}</div>}
                </div>
                {loc.phone && (
                  <a href={`tel:${loc.phone.replace(/\s/g, '')}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--text-muted)', textDecoration: 'none' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.7 }}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z" /></svg>
                    <span className="tnum">{loc.phone}</span>
                  </a>
                )}
                {loc.email && (
                  <a href={`mailto:${loc.email}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: 'var(--text-muted)', minWidth: 0, textDecoration: 'none' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.7 }}><rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22,6 12,13 2,6" /></svg>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{loc.email}</span>
                  </a>
                )}
              </div>
            ) : (
              <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--text-muted)' }}>
                No contact recorded. {!isReadOnly && 'Add one with Edit site so a driver knows who to call.'}
              </div>
            )}
          </Card>

          <Card title="Site details" style={{ flexShrink: 0 }}>
            {facts.map(f => (
              <div key={f.label} style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '9px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', width: 104, flexShrink: 0 }}>{f.label}</span>
                <span className="tnum" style={{ fontSize: 14, color: 'var(--text-primary)', flex: 1, textAlign: 'right' }}>{f.value}</span>
              </div>
            ))}
          </Card>

          <Card title="Recent movements" style={{ flexShrink: 0 }}>
            {events.length === 0 ? (
              <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--text-muted)' }}>Nothing has moved to or from this site yet.</div>
            ) : events.map((e, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                <span style={{ flexShrink: 0, width: 7, height: 7, borderRadius: 9999, marginTop: 6, background: e.tone === 'info' ? 'var(--info-text)' : 'var(--text-subtle)' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, lineHeight: '18px', color: 'var(--text-primary)' }}>{e.text}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>{formatDate(e.when)}</div>
                </div>
              </div>
            ))}
          </Card>
        </div>
      </div>
    </div>
  );
}

export default LocationDetail;
